import * as Yup from 'yup';
import axios from 'axios';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Form, FormikProvider, useFormik } from 'formik';
import {
  Container,
  Grid,
  Stack,
  Box,
  Button,
  Card,
  TextField,
  Autocomplete,
  CardHeader,
  CircularProgress,
  Typography,
  IconButton,
  Divider,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Chip,
  Tooltip,
} from '@material-ui/core';
import { useTheme, experimentalStyled as styled } from '@material-ui/core/styles';
import { LoadingButton, MobileDatePicker } from '@material-ui/lab';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import { currentFacilityId, DOMAIN, REST_API, REST_API_END_POINT } from 'src/constants/Defaultvalues';
import { UploadMultiFile } from 'src/components/upload';
import { supabase } from '../../supabase/supabaseClient';
import { Delete, NoteAdd, KeyboardBackspaceOutlined, DragIndicator } from '@material-ui/icons';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Modal from 'src/components/_dashboard/blog/ProjectModel';
import Scrollbar from 'src/components/Scrollbar';
import { Icon } from '@iconify/react';
import plusFill from '@iconify/icons-eva/plus-fill';
import Paper from '@material-ui/core/Paper';
import { fUSPrice } from 'src/utils/formatNumber';
import getLabourCostServiceTabLabel from 'src/utils/getLabourCostServiceTabLabel';

const LabelStyle = styled(Typography)(({ theme }) => ({
  ...theme.typography.subtitle2,
}));

const LabelStyleHead = styled(Typography)(({ theme }) => ({
  ...theme.typography.h5,
  // color: theme.palette.text.secondary,
}))

const VALUE_SERVICES = [
  { id: 1, value: 'Service Name' },
  { id: 2, value: 'Notes' },
  { id: 3, value: 'Markup (%)' },
  { id: 4, value: 'Cost (Hourly)' },
  { id: 5, value: 'Markup Cost (Hourly)' },
  { id: 6, value: 'Hours' },
  // { id: 7, value: 'Make As Task' },
  // { id: 8, value: 'Make Install Card' },
  { id: 7, value: 'Product Formula Cost'},
  { id: 8, value: 'Additional cost' },
  { id: 9, value: 'Total cost' },
  { id: 10, value: '' }
];

const markupTypes = [
  { id: 1, name: 'Wholesale Markup' },
  { id: 2, name: 'Retail Markup' }
]

const MAX_CHARACTERS = 500;
const formatProjectAddress = (project) => {
  if (!project) return 'No address available';
  const addressParts = [project.address, project.city, project.state, project.zip]
    .map((part) => (part || '').toString().trim())
    .filter(Boolean);
  return addressParts.length > 0 ? addressParts.join(', ') : 'No address available';
};

const selectInputValue = (event) => {
  const input = event.target;
  setTimeout(() => {
    if (input?.select) {
      input.select();
    }
  }, 0);
};

// Alphanumeric comparison helper function (natural sort)
function alphanumericCompare(str1, str2) {
  const normalize = (str) => {
    if (str === null || str === undefined) return '';
    // Strip leading and trailing quotes before comparison
    let normalized = str.toString().trim();
    // Remove leading and trailing quotes (both single and double)
    normalized = normalized.replace(/^["']+|["']+$/g, '');
    return normalized;
  };

  const a = normalize(str1);
  const b = normalize(str2);

  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;

  const getChunks = (str) => {
    const chunks = [];
    let currentChunk = '';
    let chunkType = null; // 'hash', 'otherSpecial', 'digit', or 'letter'

    for (let i = 0; i < str.length; i++) {
      const char = str[i];
      const isDigit = /\d/.test(char);
      const isLetter = /[a-zA-Z]/.test(char);
      const isHash = char === '#';
      const isOtherSpecial = !isDigit && !isLetter && !isHash;
      
      // Determine chunk type with priority: hash < otherSpecial < digit < letter
      let currentType;
      if (isHash) {
        currentType = 'hash';
      } else if (isOtherSpecial) {
        currentType = 'otherSpecial';
      } else if (isDigit) {
        currentType = 'digit';
      } else {
        currentType = 'letter';
      }

      if (i === 0) {
        chunkType = currentType;
        currentChunk = char;
      } else if (currentType === chunkType) {
        currentChunk += char;
      } else {
        chunks.push({
          value: chunkType === 'digit' ? parseInt(currentChunk, 10) : currentChunk.toLowerCase(),
          type: chunkType
        });
        currentChunk = char;
        chunkType = currentType;
      }
    }

    if (currentChunk) {
      chunks.push({
        value: chunkType === 'digit' ? parseInt(currentChunk, 10) : currentChunk.toLowerCase(),
        type: chunkType
      });
    }

    return chunks;
  };

  const chunksA = getChunks(a);
  const chunksB = getChunks(b);
  const len = Math.min(chunksA.length, chunksB.length);

  // Type priority: hash (0) < otherSpecial (1) < digit (2) < letter (3)
  const getTypePriority = (type) => {
    if (type === 'hash') return 0;
    if (type === 'otherSpecial') return 1;
    if (type === 'digit') return 2;
    return 3; // letter
  };

  for (let i = 0; i < len; i++) {
    const chunkA = chunksA[i];
    const chunkB = chunksB[i];

    const priorityA = getTypePriority(chunkA.type);
    const priorityB = getTypePriority(chunkB.type);

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // Same type, compare values
    if (chunkA.type === 'digit') {
      if (chunkA.value < chunkB.value) return -1;
      if (chunkA.value > chunkB.value) return 1;
    } else {
      if (chunkA.value < chunkB.value) return -1;
      if (chunkA.value > chunkB.value) return 1;
    }
  }

  if (chunksA.length < chunksB.length) return -1;
  if (chunksA.length > chunksB.length) return 1;

  return 0;
}

export default function RequestEstimate({ allClientsData, projectTypesData, open, setOpen }) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState([]);
  const [currentDate] = useState(moment().unix());
  const [signAgreement, setSignAgreement] = useState([]);
  // Project and client related states
  const [projectTypes, setProjectTypes] = useState(projectTypesData || []);
  const [projectCategories, setProjectCategories] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [clientCommunities, setClientCommunities] = useState([]);
  const [allCommunities, setAllCommunities] = useState([]);
  const [salesRepName, setSalesRepName] = useState('');
  const [salesRepId, setSalesRepId] = useState(null);
  const [salesRepOptions, setSalesRepOptions] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [teamMembersOptions, setTeamMembersOptions] = useState([]);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);
  const [allClients, setAllClients] = useState(allClientsData || []);
  const [isCommunityLoading, setIsCommunityLoading] = useState(false);
  const [mapServices, setMapServices] = useState([]);
  const [mapServicesLoading, setMapServicesLoading] = useState(false);
  const [taxCategories, setTaxCategories] = useState([]);
  const [selectedTaxCategory, setSelectedTaxCategory] = useState(null);
  const [openBackConfirm, setOpenBackConfirm] = useState(false);

  const [labourCostServices, setLabourCostServices] = useState([])
  const [allEstimators, setAllEstimators] = useState([])
  const [selectedEstimators, setSelectedEstimators] = useState([])
  const [selectedServices, setSelectedServices] = useState([]);
  const [invoiceSelectedServices, setInvoiceSelectedServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([])
  const [requestServices, setRequestServices] = useState([])
  const [additionalSelectedServices, setAdditionalSelectedServices] = useState([]);
  const [totalProductLabourCost, setTotalProductLabourCost] = useState(0)
  const [totalLabourCost, setTotalLabourCost] = useState(0)
  const [serviceIndexCounter, setServiceIndexCounter] = useState(0);
  const [openServiceNote, setOpenServiceNote] = useState({});
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [isClearingNote, setIsClearingNote] = useState(false);
  const [currentNote, setCurrentNote] = useState('');
  const [productRows, setProductRows] = useState([{ indexId: 1, product_name: '', product_desc: '', price: 0, retail_price: 0, is_product_row: 1, backendId: null, is_line_catalog:1 }]);
  const [serviceRows, setServiceRows] = useState([{ indexId: 1, service_name: '', service_desc: '', price: 0, retail_price: 0, is_service_row: 1, backendId: null }]);
  const [rowErrors, setRowErrors] = useState({});
  const [indexCounter, setIndexCounter] = useState(1);
  const [createdRequestId, setCreatedRequestId] = useState(null); // Track auto-created request for product row auto-save
  const [autoSavingField, setAutoSavingField] = useState(null); // Track which field is being auto-saved: { indexId, field: 'name' | 'desc' }
  const originalValuesRef = useRef({}); // Track original values when field is focused
  const debounceTimerRef = useRef(null); // Debounce timer for auto-save after typing
  const autoSavingFieldRef = useRef(null); // Synchronous ref for checking if currently saving
  
  // User info
  let user = JSON.parse(localStorage.getItem('user'));
  let isClient = user?.isClient || user?.isclient;
  let userType = isClient ? 3 : user.user_type;
  let userId = user?.user_id;

  // Keep local projectTypes in sync with parent-provided data (Quotation.js).
  useEffect(() => {
    if (Array.isArray(projectTypesData)) {
      setProjectTypes(projectTypesData);
    }
  }, [projectTypesData]);

  if (user.added_by_client && user.parent_user_type && user.parent_id) {
    userId = user.parent_id;
    userType = 3;
  }

  const explodedPermissions = user?.exploded_permissions;

  const fetchAllAgreements = async () => {
    try {
      const response = await axios.post(`${REST_API_END_POINT}agreement/get-all-agreements`);
      if (response.data.status === 1 && Array.isArray(response.data.result)) {
        setSignAgreement(response.data.result); // Set signAgreement only if it's an array
      } else {
        setSignAgreement([]); // Empty signAgreement if the result is not valid
      }
    } catch (error) {
      console.log(error, 'Error in fetching all agreements');
      setSignAgreement([]); // Ensure signAgreement is an empty array on error
    }
  };

  useEffect(() => {
    fetchAllAgreements();
  }, []);

  // Fetch all clients
  const fetchAllClients = async () => {
    try {
      const res = await axios.post(`${REST_API}webservice/clients/getAllClients`, {
        userType,
        userId: userId,
        order: true
      });
      let data = res.data?.customer;
      await setAllClients(data || []);
    } catch (error) {
      console.log(error, 'Error in fetching all clients', error);
    }
  };

  // Fetch community by client
  const fetchAllCommunityByClient = async (clientId) => {
    console.log("####################ClientIs", clientId);
    setIsCommunityLoading(true);
    try {
      clientId = clientId.id;
      const res = await axios.post(`${REST_API}webservice/rfq/get-community-by-client`, { clientId });
      let data = res.data?.community;
      // Sort communities alphanumerically by name
      const sortedCommunities = [...(data || [])].sort((a, b) => alphanumericCompare(a?.name || '', b?.name || ''))
      await setCommunities(sortedCommunities);
      // Store all communities for later use in getSalesRepOptionsFromCommunity
      setAllCommunities(sortedCommunities)
    } catch (error) {
      console.log(error, 'Error in fetching all clients');
    } finally {
      setIsCommunityLoading(false);
    }
  };

  const fetchTeamMembersForCollaborators = async () => {
    setTeamMembersLoading(true);
    try {
      const res = await axios.post(`${REST_API}webservice/teams/get-all-teams-data-collaborators`, {
        usersId: userId,
        usersType: userType,
        isClient: isClient || false,
        page: 1,
        rowsPerPage: 5000,
        orderBy: 'name',
        order: 'asc',
      });
      const raw = res.data?.team_members?.data || res.data?.team_members || [];
      setTeamMembersOptions(Array.isArray(raw) ? raw : []);
    } catch (error) {
      setTeamMembersOptions([]);
    } finally {
      setTeamMembersLoading(false);
    }
  };

  const getSalesRepOptionsFromCommunity = (community) => {
    // Use ?? so an empty eligible list does not fall back to raw stored IDs.
    const rawNames = community?.all_sales_rep_names ?? community?.sales_rep_names;
    const rawIds = community?.all_sales_rep_ids ?? community?.sales_rep_ids;

    const names = Array.isArray(rawNames)
      ? rawNames
      : typeof rawNames === 'string'
        ? rawNames.split(',').map((s) => s.trim()).filter(Boolean)
        : [];

    const ids = Array.isArray(rawIds)
      ? rawIds
      : typeof rawIds === 'string'
        ? rawIds.split(',').map((s) => s.trim()).filter(Boolean)
        : [];

    return names.map((name, idx) => ({
      id: ids[idx] ?? null,
      name,
    }));
  };

  const getCommunitySalesRepIds = (community) => {
    const rawIds = community?.all_sales_rep_ids ?? community?.sales_rep_ids;
    if (!rawIds) {
      return [];
    }

    const ids = Array.isArray(rawIds)
      ? rawIds
      : typeof rawIds === 'string'
        ? rawIds.split(',')
        : [];

    return ids
      .map((id) => String(id ?? '').trim())
      .filter(Boolean);
  };

  const calculateTotal = (price, quantity, discount, location = 1, adjustment = 0, markAdjType = 1) => {
    const parsedQuantity = parseFloat(quantity) || 0;
    const parsedDiscount = parseFloat(discount) || 0;
    const locationQuantity = parseFloat(location) || 1;
    const newPrice = parseFloat(price) || 0;
  
    let SPMA = newPrice + ((newPrice * (adjustment ? Number(adjustment) : 0)) / 100);
  
    if (markAdjType == 2 || markAdjType == 3) {
      SPMA = newPrice + (adjustment ? Number(adjustment) : 0);
    }
  
    let subtotal = SPMA * parsedQuantity * locationQuantity;
  
    if (parsedDiscount) {
      subtotal -= parsedDiscount;
    }
  
    return parseFloat(subtotal.toFixed(2)); // Ensures a clean number
  };

  // Service notes handlers
  const handleOpenServiceNote = (indexId, note) => {
    setCurrentNote(note || '');
    setOpenServiceNote((prev) => ({ ...prev, [indexId]: true }));
  };

  const handleCloseServiceNote = (indexId) => {
    setOpenServiceNote((prev) => ({ ...prev, [indexId]: false }));
    setCurrentNote('');
  };

  const handleDeleteService = (servId, indexId) => {
    // const newFilteredServices = additionalSelectedServices?.filter(data => data.id !== servId);
    const newFilteredServices = additionalSelectedServices?.filter((data) => {
      if (data?.isAdditionalProduct) {
        return data?.newIndexId !== indexId;
      }
      return !(data.id == servId && (data.newIndexId == indexId || data.indexId == indexId));
    });
    // const allFilteredServices = filteredServices?.find(data => data.id !== servId);
    setFieldValue('services', newFilteredServices);
    setAdditionalSelectedServices(newFilteredServices);
  };

  const handleServiceNoteChange = (event) => {
    const newNote = event.target.value;
    if (newNote.length <= MAX_CHARACTERS) {
      setCurrentNote(newNote);
    }
  };

  const handleSaveServiceNote = (indexId) => {
    setIsSavingNote(true);
    setTimeout(() => {
      // Update additionalSelectedServices
      setAdditionalSelectedServices((prev) =>
        prev.map((service) =>
          (service.indexId === indexId || service.newIndexId === indexId || service.id === indexId) ? { ...service, service_notes: currentNote } : service
        )
      );
      setIsSavingNote(false);
      handleCloseServiceNote(indexId);
    }, 100);
  };

  // Clear service note and save empty value
  const handleClearServiceNote = (indexId) => {
    setIsClearingNote(true);
    setTimeout(() => {
      setAdditionalSelectedServices((prev) =>
        prev.map((service) =>
          (service.indexId === indexId || service.newIndexId === indexId || service.id === indexId) ? { ...service, service_notes: '' } : service
        )
      );
      setCurrentNote('');
      setIsClearingNote(false);
    }, 100);
  };

  // Product rows handlers
  const addNewProductRow = () => {
    // const newIndexCounter = indexCounter + 1;
    const newIndexCounter = Math.max(...productRows.map((row) => row.indexId), 0) + 1; // Find the highest indexId and add 1
    setIndexCounter(newIndexCounter);
    
    const newProduct = {
      indexId: newIndexCounter,
      product_name: '',
      product_desc: '',
      price: 0,
      retail_price: 0,
      is_product_row: 1,
      backendId: null,
      is_line_catalog:1,
    };
    
    setProductRows((prevRows) => [...prevRows, newProduct]);
  };

  const addNewServiceRow = () => {
    // const newIndexCounter = indexCounter + 1;
    const newIndexCounter = Math.max(...serviceRows.map((row) => row.indexId), 0) + 1; // Find the highest indexId and add 1
    setIndexCounter(newIndexCounter);
    
    const newProduct = {
      indexId: newIndexCounter,
      service_name: '',
      service_desc: '',
      price: 0,
      retail_price: 0,
      is_service_row: 1,
      backendId: null
    };
    
    setServiceRows((prevRows) => [...prevRows, newProduct]);
  };

  const handleDeleteProduct = (indexId) => {
    if (productRows?.length > 1) {
      // setProductRows((prevRows) => prevRows.filter(row => row.indexId !== indexId));
      const updatedRows = productRows?.filter((row) => row.indexId !== indexId);

      // Remove the error for the deleted row (if it exists)
      setRowErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[indexId];  // Delete the error for the deleted row
        return newErrors;
      });

      // Reassign the indexId (Sl No) to the remaining rows
      const renumberedRows = updatedRows?.map((row, idx) => ({
        ...row,
        indexId: idx + 1,  // Recalculate Sl No based on row order
      }));

      setProductRows(renumberedRows);
      setIndexCounter(renumberedRows?.length + 1);
    }else{
      setProductRows([{ indexId: 1, product_name: '', product_desc: '', price: 0, retail_price: 0, is_product_row: 1, backendId: null, is_line_catalog:1 }]);
      setIndexCounter(1);
    }
  };

  const handleProductRowsDragEnd = useCallback(
    (result) => {
      if (!result.destination || result.destination.index === result.source.index) {
        return
      }
      const sorted = [...productRows]
      const [moved] = sorted.splice(result.source.index, 1)
      sorted.splice(result.destination.index, 0, moved)
      const indexIdMap = {}
      sorted.forEach((row, idx) => {
        indexIdMap[String(row.indexId)] = idx + 1
      })
      const remappedRows = sorted.map((row) => ({
        ...row,
        indexId: indexIdMap[String(row.indexId)],
      }))
      setRowErrors((prev) => {
        const next = {}
        Object.keys(prev || {}).forEach((oldKey) => {
          const newKey = indexIdMap[oldKey]
          if (newKey !== undefined) next[newKey] = prev[oldKey]
        })
        return next
      })
      setProductRows(remappedRows)
      setIndexCounter(remappedRows.length + 1)
    },
    [productRows],
  )

  const handleServiceRowsDragEnd = useCallback(
    (result) => {
      if (!result.destination || result.destination.index === result.source.index) {
        return
      }
      const sorted = [...serviceRows]
      const [moved] = sorted.splice(result.source.index, 1)
      sorted.splice(result.destination.index, 0, moved)
      const indexIdMap = {}
      sorted.forEach((row, idx) => {
        indexIdMap[String(row.indexId)] = idx + 1
      })
      const remappedRows = sorted.map((row) => ({
        ...row,
        indexId: indexIdMap[String(row.indexId)],
      }))
      setRowErrors((prev) => {
        const next = {}
        Object.keys(prev || {}).forEach((oldKey) => {
          const newKey = indexIdMap[oldKey]
          if (newKey !== undefined) next[newKey] = prev[oldKey]
        })
        return next
      })
      setServiceRows(remappedRows)
    },
    [serviceRows],
  )

  const handleProductNameChange = (indexId, value) => {
    setProductRows((prevRows) => 
      prevRows.map(row => 
        row.indexId === indexId ? { ...row, product_name: value, price: 0, retail_price: 0, is_product_row: 1, is_line_catalog:1 } : row
      )
    );
  };
  const handleServiceNameChange = (indexId, value) => {
    setServiceRows((prevRows) => 
      prevRows.map(row => 
        row.indexId === indexId ? { ...row, service_name: value, price: 0, retail_price: 0, is_service_row: 1 } : row
      )
    );
  };
  const handleProductDescChange = (indexId, value) => {
    setProductRows((prevRows) => 
      prevRows.map(row => 
        row.indexId === indexId ? { ...row, product_desc: value, price: 0, retail_price: 0, is_product_row: 1, is_line_catalog:1 } : row
      )
    );
  };

  const handleServiceDescChange = (indexId, value) => {
    setServiceRows((prevRows) => 
      prevRows.map(row => 
        row.indexId === indexId ? { ...row, service_desc: value, price: 0, retail_price: 0, is_service_row: 1 } : row
      )
    );
  };

  // Store original value when field is focused
  const handleFieldFocus = (indexId, fieldType, value) => {
    const key = `${indexId}-${fieldType}`;
    originalValuesRef.current[key] = value || '';
  };

  // Check if value has changed from original
  const hasFieldChanged = (indexId, fieldType, currentValue) => {
    const key = `${indexId}-${fieldType}`;
    const originalValue = originalValuesRef.current[key] || '';
    return (currentValue || '') !== originalValue;
  };

  // Clear original value after save
  const clearOriginalValue = (indexId, fieldType) => {
    const key = `${indexId}-${fieldType}`;
    delete originalValuesRef.current[key];
  };

  // Debounced auto-save trigger
  const triggerDebouncedAutoSave = (row, fieldType) => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set new timer for 1.5 seconds after typing stops
    debounceTimerRef.current = setTimeout(() => {
      handleProductRowAutoSave(row, fieldType, true);
    }, 1500);
  };

  const triggerDebouncedAutoSaveService = (row, fieldType) => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set new timer for 1.5 seconds after typing stops
    debounceTimerRef.current = setTimeout(() => {
      handleServiceRowAutoSave(row, fieldType, true);
    }, 1500);
  };
  // Delete auto-created draft estimate and its product rows
  const handleDeleteDraftEstimate = async () => {
    if (!createdRequestId) return;

    try {
      await axios.post(`${REST_API_END_POINT}request/delete-draft-estimate`, {
        requestId: createdRequestId,
        userId: user.user_id,
        isRequestEstimate: true
      });

      console.log('Draft estimate deleted successfully');
    } catch (error) {
      console.error('Error deleting draft estimate:', error);
    }
  };
  // Auto-save product row to backend on blur or after typing stops
  // fieldType: 'name' or 'desc' to show specific message
  // fromDebounce: true if called from debounce timer
  const handleProductRowAutoSave = async (row, fieldType = 'name', fromDebounce = false) => {
    // Clear debounce timer if called from blur
    if (!fromDebounce && debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Don't auto-save if both name and description are empty
    if ((row.product_name || '').trim() === '' && (row.product_desc || '').trim() === '') {
      return;
    }

    // Check if there are actual changes
    const currentValue = fieldType === 'name' ? row.product_name : row.product_desc;
    if (!hasFieldChanged(row.indexId, fieldType, currentValue)) {
      return; // No changes, don't save
    }

    // Don't auto-save if already saving (use ref for synchronous check)
    if (autoSavingFieldRef.current) return;

    // Set ref first for synchronous checking, then state for UI update
    const savingField = { indexId: row.indexId, field: fieldType };
    autoSavingFieldRef.current = savingField;
    setAutoSavingField(savingField);

    try {
      // If we don't have a createdRequestId yet, we need to create a draft request first
      let requestIdToUse = createdRequestId;

      if (!requestIdToUse) {
        // Create a minimal draft request to get a requestId
        const draftRequestData = {
          values: {
            name: 'Draft Estimate',
            description: 'Auto-created draft for product auto-save',
            start: moment().unix(),
            end: moment().unix(),
            clients: null,
            project_categories: null,
            project_type: null,
            map_service: null,
            community: null,
            labour_cost: 0,
            budget: 0,
            invoice_discount: null,
            total: 0,
            taxAmount: 0,
            avatarUrl: [],
            products: [],
            productRows: [],
            selectedServices: [],
            additionalSelectedServices: [],
            markup_type: { id: 2, name: 'Retail Markup' }
          },
          userId,
          userType,
          isClient,
          userName: user?.first_name || '',
          quotation: 1,
          rfpProducts: [],
          rfpServices: [],
          materials: [],
          selectedEstimators: [],
          isRequestEstimate: true,
          isDraft: true // Mark as draft
        };

        const draftRes = await axios.post(`${REST_API}webservice/request/add-request`, draftRequestData);
        
        if (draftRes.data?.result) {
          requestIdToUse = draftRes.data.result;
          setCreatedRequestId(requestIdToUse);
          console.log('Draft request created with ID:', requestIdToUse);
        } else {
          console.error('Failed to create draft request');
          autoSavingFieldRef.current = null;
          setAutoSavingField(null);
          return;
        }
      }

      const successMessage = fieldType === 'name' ? 'Product name auto saved' : 'Product description auto saved';

      // Now auto-save the product row
      if (!row.backendId) {
        // New row - create it in backend
        const response = await axios.post(`${REST_API_END_POINT}request/update-product-row-estimate`, {
          requestId: requestIdToUse,
          productRow: row,
          action: 'create',
          userId: user.user_id
        });

        if (response.data.status === 1 && response.data.data?.backendId) {
          // Update the row with the new backendId
          setProductRows(prevRows =>
            prevRows.map(r =>
              r.indexId === row.indexId ? { ...r, backendId: response.data.data.backendId } : r
            )
          );
          enqueueSnackbar(successMessage, { variant: 'success' });
        } else {
          console.error('Failed to auto-save new product row:', response.data);
        }
      } else {
        // Existing row - update it in backend
        const response = await axios.post(`${REST_API_END_POINT}request/update-product-row-estimate`, {
          requestId: requestIdToUse,
          productRow: row,
          action: 'update',
          userId: user.user_id
        });

        if (response.data.status === 1) {
          enqueueSnackbar(successMessage, { variant: 'success' });
        } else {
          console.error('Failed to auto-save product row:', response.data);
        }
      }
    } catch (error) {
      console.error('Error auto-saving product row:', error);
      enqueueSnackbar('Error auto-saving product row.', { variant: 'error' });
    } finally {
      // Update original value to current value after save (success or fail)
      const currentValue = fieldType === 'name' ? row.product_name : row.product_desc;
      originalValuesRef.current[`${row.indexId}-${fieldType}`] = currentValue || '';
      autoSavingFieldRef.current = null;
      setAutoSavingField(null);
    }
  };
  const handleServiceRowAutoSave = async (row, fieldType = 'name', fromDebounce = false) => {
    // Clear debounce timer if called from blur
    if (!fromDebounce && debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Don't auto-save if both name and description are empty
    if ((row.service_name || '').trim() === '' && (row.service_desc || '').trim() === '') {
      return;
    }

    // Check if there are actual changes
    const currentValue = fieldType === 'name' ? row.service_name : row.service_desc;
    if (!hasFieldChanged(row.indexId, fieldType, currentValue)) {
      return; // No changes, don't save
    }

    // Don't auto-save if already saving (use ref for synchronous check)
    if (autoSavingFieldRef.current) return;

    // Set ref first for synchronous checking, then state for UI update
    const savingField = { indexId: row.indexId, field: fieldType };
    autoSavingFieldRef.current = savingField;
    setAutoSavingField(savingField);

    try {
      // If we don't have a createdRequestId yet, we need to create a draft request first
      let requestIdToUse = createdRequestId;

      if (!requestIdToUse) {
        // Create a minimal draft request to get a requestId
        const draftRequestData = {
          values: {
            name: 'Draft Estimate',
            description: 'Auto-created draft for product auto-save',
            start: moment().unix(),
            end: moment().unix(),
            clients: null,
            project_categories: null,
            project_type: null,
            map_service: null,
            community: null,
            labour_cost: 0,
            budget: 0,
            invoice_discount: null,
            total: 0,
            taxAmount: 0,
            avatarUrl: [],
            products: [],
            productRows: [],
            selectedServices: [],
            additionalSelectedServices: [],
            markup_type: { id: 2, name: 'Retail Markup' }
          },
          userId,
          userType,
          isClient,
          userName: user?.first_name || '',
          quotation: 1,
          rfpProducts: [],
          rfpServices: [],
          materials: [],
          selectedEstimators: [],
          isRequestEstimate: true,
          isDraft: true // Mark as draft
        };

        const draftRes = await axios.post(`${REST_API}webservice/request/add-request`, draftRequestData);
        
        if (draftRes.data?.result) {
          requestIdToUse = draftRes.data.result;
          setCreatedRequestId(requestIdToUse);
          console.log('Draft request created with ID:', requestIdToUse);
        } else {
          console.error('Failed to create draft request');
          autoSavingFieldRef.current = null;
          setAutoSavingField(null);
          return;
        }
      }

      const successMessage = fieldType === 'name' ? 'Servicename auto saved' : 'Service description auto saved';

      // Now auto-save the product row
      if (!row.backendId) {
        // New row - create it in backend
        const response = await axios.post(`${REST_API_END_POINT}request/update-service-row-estimate`, {
          requestId: requestIdToUse,
          productRow: row,
          action: 'create',
          userId: user.user_id
        });

        if (response.data.status === 1 && response.data.data?.backendId) {
          // Update the row with the new backendId
          setServiceRows(prevRows =>
            prevRows.map(r =>
              r.indexId === row.indexId ? { ...r, backendId: response.data.data.backendId } : r
            )
          );
          enqueueSnackbar(successMessage, { variant: 'success' });
        } else {
          console.error('Failed to auto-save new servicerow:', response.data);
        }
      } else {
        // Existing row - update it in backend
        const response = await axios.post(`${REST_API_END_POINT}request/update-service-row-estimate`, {
          requestId: requestIdToUse,
          productRow: row,
          action: 'update',
          userId: user.user_id
        });

        if (response.data.status === 1) {
          enqueueSnackbar(successMessage, { variant: 'success' });
        } else {
          console.error('Failed to auto-save service row:', response.data);
        }
      }
    } catch (error) {
      console.error('Error auto-saving service row:', error);
      enqueueSnackbar('Error auto-saving service row.', { variant: 'error' });
    } finally {
      // Update original value to current value after save (success or fail)
      const currentValue = fieldType === 'name' ? row.service_name : row.service_desc;
      originalValuesRef.current[`${row.indexId}-${fieldType}`] = currentValue || '';
      autoSavingFieldRef.current = null;
      setAutoSavingField(null);
    }
  };

  // Delete product row from backend and frontend
  const handleDeleteRequestEstimateProduct = async (indexId, backendId = null) => {
    // Remove the errors
    setRowErrors({});

    // If row has a backendId and we have a createdRequestId, delete from backend first
    if (backendId && createdRequestId) {
      try {
        const response = await axios.post(`${REST_API_END_POINT}request/update-product-row-estimate`, {
          requestId: createdRequestId,
          productRow: { backendId },
          action: 'delete',
          userId: user.user_id
        });

        if (response.data.status !== 1) {
          enqueueSnackbar('Failed to delete product row from server.', { variant: 'error' });
          console.error('Failed to delete product row:', response.data);
          return;
        }
        enqueueSnackbar('Product row deleted successfully!', { variant: 'success' });
      } catch (error) {
        console.error('Error deleting product row:', error);
        enqueueSnackbar('Error deleting product row.', { variant: 'error' });
        return;
      }
    }

    // Update frontend state using functional update to avoid stale closure issues
    setProductRows((prevRows) => {
      if (prevRows.length > 1) {
        const updatedRows = prevRows.filter((row) => {
          if (backendId && row.backendId) {
            return row.backendId !== backendId;
          }
          return row.indexId !== indexId;
        });

        // Reassign the indexId (Sl No) to the remaining rows
        const renumberedRows = updatedRows.map((row, idx) => ({
          ...row,
          indexId: idx + 1,
        }));

        // Update indexCounter after state update
        setTimeout(() => setIndexCounter(renumberedRows.length + 1), 0);
        
        return renumberedRows;
      } else {
        setTimeout(() => setIndexCounter(1), 0);
        return [{ indexId: 1, product_name: '', product_desc: '', price: 0, retail_price: 0, is_product_row: 1, backendId: null , is_line_catalog:1}];
      }
    });

    // Remove the error for the deleted row (if it exists)
    setRowErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors[indexId];
      return newErrors;
    });
  };

  const handleDeleteRequestEstimateService = async (indexId, backendId = null) => {
    // Remove the errors
    setRowErrors({});

    // If row has a backendId and we have a createdRequestId, delete from backend first
    if (backendId && createdRequestId) {
      try {
        const response = await axios.post(`${REST_API_END_POINT}request/update-service-row-estimate`, {
          requestId: createdRequestId,
          productRow: { backendId },
          action: 'delete',
          userId: user.user_id
        });

        if (response.data.status !== 1) {
          enqueueSnackbar('Failed to delete service row from server.', { variant: 'error' });
          console.error('Failed to delete service row:', response.data);
          return;
        }
        enqueueSnackbar('Product row deleted successfully!', { variant: 'success' });
      } catch (error) {
        console.error('Error deleting service row:', error);
        enqueueSnackbar('Error deleting service row.', { variant: 'error' });
        return;
      }
    }

    // Update frontend state using functional update to avoid stale closure issues
    setServiceRows((prevRows) => {
      if (prevRows.length > 1) {
        const updatedRows = prevRows.filter((row) => {
          if (backendId && row.backendId) {
            return row.backendId !== backendId;
          }
          return row.indexId !== indexId;
        });

        // Reassign the indexId (Sl No) to the remaining rows
        const renumberedRows = updatedRows.map((row, idx) => ({
          ...row,
          indexId: idx + 1,
        }));

        // Update indexCounter after state update
        setTimeout(() => setIndexCounter(renumberedRows.length + 1), 0);
        
        return renumberedRows;
      } else {
        setTimeout(() => setIndexCounter(1), 0);
        return [{ indexId: 1, service_name: '', service_desc: '', price: 0, retail_price: 0, is_service_row: 1, backendId: null }];
      }
    });

    // Remove the error for the deleted row (if it exists)
    setRowErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors[indexId];
      return newErrors;
    });
  };

  // Fetch client-specific projects for clients
  const fetchClientSpecificProjects = async () => {
    console.log('RequestEstimate fetchClientSpecificProjects called with:', { userId, userType, isClient, clientId: user?.client_id });
    setIsCommunityLoading(true);
    try {
      const res = await axios.post(`${REST_API}webservice/rfq/get-communities-for-client-autocomplete`, {
        userId: userId,
        userType: userType,
        isClient: isClient,
        clientId: user?.client_id
      });
      
      console.log('RequestEstimate API response:', res.data);
      if (res.data?.status === 1) {
        // Sort client communities alphanumerically by name
        const sortedClientCommunities = [...(res.data.data || [])].sort((a, b) => alphanumericCompare(a?.name || '', b?.name || ''))
        setClientCommunities(sortedClientCommunities);
        console.log('RequestEstimate Set clientCommunities to:', sortedClientCommunities);
      } else {
        setClientCommunities([]);
        console.log('RequestEstimate API returned status 0, setting empty clientCommunities');
      }
    } catch (error) {
      console.log('RequestEstimate Error fetching client-specific projects:', error);
      setClientCommunities([]);
    } finally {
      setIsCommunityLoading(false);
    }
  };

const fetchProducts = async () => {
    try {
      const productsRes = await axios.post(`${REST_API}webservice/request/get-labour-cost-services`,{});
      // Sort services alphanumerically by name
      const sortedServices = [...(productsRes.data.labourCostServices || [])].sort((a, b) => alphanumericCompare(a?.name || '', b?.name || ''))
      setLabourCostServices(sortedServices);
      setFilteredServices(sortedServices);
    } catch (productsError) {
      console.error('Error fetching products:', productsError);
      setLabourCostServices([]);
      setFilteredServices([]);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Fetch all map services
  const fetchAllMapServices = async () => {
    setMapServicesLoading(true);
    try {
      const res = await axios.post(`${REST_API}webservice/route/fetch-routes-with-locations`);
      let data = res.data?.routeData;
      if (data) {
        const newData = data?.filter((i) => i.locations != null);
        if (newData) {
          setMapServices(newData);
        } else {
          setMapServices([]);
        }
      } else {
        setMapServices([]);
      }
    } catch (error) {
      setMapServices([]);
      console.log(error, 'Error in fetching map services');
    } finally {
      setMapServicesLoading(false);
    }
  };

  // Fetch tax category
  const fetchTaxCategory = async () => {
    try {
      const response = await axios.post(`${REST_API}webservice/product/get-tax-category`);
      if (response.data.status === 1) {
        setTaxCategories(response.data.taxResult);
      } else {
        setTaxCategories([]);
      }
    } catch (error) {
      console.log("Error - ", error);
      setTaxCategories([]);
    }
  };

  // Initial data fetch - trigger when component opens
  useEffect(() => {
    if (!open) return;
    
    const fetchData = async () => {
      try {
        const response = await axios.post(`${REST_API_END_POINT}project/get-data-for-add-project`, {
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (response.data.status === 1) {
          setProjectTypes(response.data.projectTypes || []);
          const category = response.data.projectCategories;
          const filteredCategory = category?.filter(item => item.id == 14) || [];
          setProjectCategories(filteredCategory);
          // Sort communities alphanumerically by name
          const sortedCommunities = [...(response.data.community || [])].sort((a, b) => alphanumericCompare(a?.name || '', b?.name || ''))
          setCommunities(sortedCommunities);
          // Sort estimators alphanumerically by name
          const sortedEstimators = [...(response.data.allEstimators || [])].sort((a, b) => alphanumericCompare(a?.name || '', b?.name || ''))
          setAllEstimators(sortedEstimators)
        }
      } catch (error) {
        console.error("Error fetching project data:", error);
      }
      
      // try {
      //   await fetchAllMapServices();
      // } catch (error) {
      //   console.error("Error fetching map services:", error);
      // }
      
      try {
        await fetchAllClients();
      } catch (error) {
        console.error("Error fetching clients:", error);
      }
      try {
        await fetchTeamMembersForCollaborators();
      } catch (error) {
        console.error("Error fetching team members:", error);
      }
      
      // try {
      //   await fetchTaxCategory();
      // } catch (error) {
      //   console.error("Error fetching tax category:", error);
      // }
    };
    
    fetchData();
  }, [open]);

  useEffect(()=>{
    const allFilteredServices = filteredServices?.filter(item => item.service_type == 44 && item.status === 1)
    // Sort the services based on their id (ascending order)
    const sortedServices = allFilteredServices?.sort((a, b) => a.id - b.id);
    // Map over the newly selected services and preserve existing properties
    const updatedServices = sortedServices?.map((service, index) => {
      if (index === 0) {
        return {
          ...service,
          newIndexId: index + 1,
          quantity: 1,
          note: '' // Initialize note as empty string
        };
      }
      return null; // Exclude other services by returning null or simply omit them
    })?.filter(service => service !== null) || [];
    console.log('indexindexindex==3',updatedServices)
    // Update the service index counter for new services
    if (updatedServices.length > 0) {
      const maxIndexId = Math.max(
        ...updatedServices.map(s => s.newIndexId || 0),
        serviceIndexCounter
      );
      setServiceIndexCounter(maxIndexId);
    }
    // Update Formik's 'services' field
    setFieldValue('services', updatedServices);
    const finalServices = [...updatedServices];
    setAdditionalSelectedServices(finalServices);
    if(finalServices?.length > 0){
      setSelectedEstimators(allEstimators || [])
    }else{
      setSelectedEstimators([])
    }
  },[filteredServices, allEstimators])

  // Load client-specific projects on component mount for clients
  useEffect(() => {
    if (!open) return;
    
    console.log('RequestEstimate useEffect triggered - isClient:', isClient, 'userType:', userType, 'userId:', userId);
    if (isClient || userType === 3) {
      console.log('RequestEstimate Calling fetchClientSpecificProjects');
      fetchClientSpecificProjects();
    }
  }, [open, isClient, userType, userId]);

  // File upload handler
  const handleDrop = async (acceptedFiles) => {
    const noPermission = !explodedPermissions?.some(
      (p) => p === 'Upload Files Sales Order'
    );
    if (noPermission && user?.added_by_client === 1) {
      enqueueSnackbar('Access Denied: You do not have sufficient permissions', {
        variant: 'error'
      });
      return;
    }
    if (!acceptedFiles || acceptedFiles.length === 0) return;

    setIsUploading(true);

    try {
      const uploadPromises = acceptedFiles.map(async (file) => {
        const timestamp = Date.now();
        const sanitizedName = file.name.replace(/\s+/g, '_');
        const fileName = `${timestamp}_${sanitizedName}`;
        const filePath = `estimate/attachments/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('signwise-attachments')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        setProgress(100);

        const { data: publicUrlData, error: urlError } = supabase.storage
          .from('signwise-attachments')
          .getPublicUrl(filePath);

        if (urlError || !publicUrlData?.publicUrl) {
          throw new Error('Failed to get public URL');
        }

        return {
          url: publicUrlData.publicUrl,
          file_type: file.type,
          extension: file.name.split('.').pop(),
          size: file.size
        };
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      setAvatarUrl((prev) => [...prev, ...uploadedFiles]);
    } catch (err) {
      console.error('Upload error:', err.message);
      enqueueSnackbar('Upload failed: ' + err.message, { variant: 'error' });
    } finally {
      setIsUploading(false);
      setProgress(null);
    }
  };

  const handleConvertToJob = async (reqId, details) => {
    try {
      let requestId = reqId
      let requestPartnerId = details?.rt_partner_id

      const data = { requestId, userId, requestPartnerId }
      const res = await axios.post(`${REST_API}webservice/request/create-or-update-estimate-job`, data);
  
      if (res.data.status == 1) {
        enqueueSnackbar('Estimate Job created successfully', { variant: 'success' });
      } else {
        enqueueSnackbar('Failed to create estimate job', { variant: 'error' });
      }
    } catch (error) {
      console.log(error, 'error in creating estimate job')
      enqueueSnackbar('Failed to create estimate job', { variant: 'error' });
    }
  }

  const handleRemoveAll = () => {
    setAvatarUrl([]);
  };

  const handleRemove = (file) => {
    const filteredItems = avatarUrl.filter((_file) => _file !== file);
    setAvatarUrl(filteredItems);
  };

  // Validation schema
  const NewUserSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    description: Yup.string().required('Description is required'),
    clients: (userType != 3 && !isClient) && Yup.object().nullable().required('Clients is required'),
    project_type: (userType != 3 && !isClient) && Yup.object().nullable().required('Job class is required'),
    map_service: Yup.mixed().when(['project_categories', 'project_type'], {
      is: (project_categories, project_type) => 
        (userType !== 3 && !isClient) && 
        project_categories?.id == 14 && 
        project_type?.name == 'TS',
      then: Yup.object().nullable().required('Please select the map service'),
      otherwise: Yup.mixed().nullable(),
    }),
    community: userType != 3
      ? Yup.object().nullable().required('Project is required,Enable projects for client')
      : Yup.object().nullable().required('Project is required'),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
      po_number: '',
      description: '',
      start: currentDate,
      end: currentDate,
      installation_start_date: null,
      installation_end_date: null,
      clients: null,
      project_categories: null,
      project_type: null,
      map_service: null,
      community: null,
      labour_cost: 0,
      budget: 0,
      invoice_discount: null,
      total: 0,
      taxAmount: 0,
    },
    validationSchema: NewUserSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        // const hasEmptyProductRowName = productRows?.some(product => (product.product_name || '').trim() === '');
        // if (hasEmptyProductRowName) {
        //   enqueueSnackbar('Please fill in all required product names.', { variant: 'error' });
        //   return;
        // }

        let errors = {};
        // Validate product rows for empty product_name when description exists
        productRows.forEach((row) => {
          if (!row.product_name.trim() && row.product_desc.trim()) {
            errors[row.indexId] = 'Product name is required when description is provided';
          }
        });

        // If errors are found, set rowErrors and stop submission
        if (Object.keys(errors).length > 0) {
          setRowErrors(errors);  // Update rowErrors state
          enqueueSnackbar('Please fill in all required product names if a description is provided.', { variant: 'error' });
          return; // Stop form submission
        }

        // const hasEmptyProductNameWithDescription = productRows?.some(
        //   (product) => 
        //     (!product.product_name || product.product_name.trim() === '') && 
        //     (product.product_desc && product.product_desc.trim() !== '')
        // );

        // if (hasEmptyProductNameWithDescription) {
        //   enqueueSnackbar('Please fill in all required product names if a description is provided.', { variant: 'error' });
        //   return;
        // }
        setIsSubmitting(true);
        let user = JSON.parse(localStorage.getItem('user'));
        const userName = user.first_name;
        const clientsArray = values.clients ? [values.clients] : [];
        values.clientsData = clientsArray;
        values.markup_type =  markupTypes[1];
        if(additionalSelectedServices?.length == 0 && userType !==3){
          enqueueSnackbar('Please choose an estimate service.', { variant: 'error' });
          return;
        }
        if(selectedEstimators?.length <= 0 && (userType !==3 && !isClient)){
          enqueueSnackbar('Please choose atleast one estimator', {variant: 'error',})
          return;
        }
        
        values.project_categories = values?.project_type?.name == 'TS' ? (values?.project_categories || null) : null;
        values.map_service = (values?.project_type?.name == 'TS' && (values?.project_categories && values?.project_categories?.id == 14)) ? values?.map_service : null;
        values.avatarUrl = avatarUrl;
        values.products = productRows?.filter(product => product.product_name?.trim() !== '') || [];
        values.productRows = productRows?.filter(product => product.product_name?.trim() !== '') || [];
        const filteredServiceRows = serviceRows?.filter(service => service.service_name?.trim() !== '') || [];
        const formattedServiceRows = filteredServiceRows.map(service => ({
          ...service,
          qty: 1,
          quantity: 1,
          unit_cost: 0,
          total_cost: 0,
          markup: 0,
          jobcost_unit_cost: 0,
          jobcost_total_cost: 0,
          facility_id : currentFacilityId
        }));
        values.serviceRows = formattedServiceRows;
        const combinedServices = [
          ...formattedServiceRows,
          ...additionalSelectedServices
        ];
        values.selectedServices = combinedServices || additionalSelectedServices || []
        values.additionalSelectedServices = combinedServices || additionalSelectedServices || []

        const requestData = {
          values,
          userId,
          taxCategory: selectedTaxCategory?.id,
          userType,
          isClient,
          userName,
          salesRepName,
          salesRepId,
          collaborators: Array.isArray(collaborators) ? collaborators.map((c) => (typeof c === 'object' && c?.id != null ? c.id : c)).filter(Boolean).join(',') : (collaborators || ''),
          quotation: 1,
          rfpProducts: [],
          rfpServices: [],
          materials: [],
          selectedEstimators,
          isRequestEstimate: true,
          existingRequestId: createdRequestId || null // Pass existing request ID if draft was auto-created
        };
        // TODO: Add API call to submit request estimate
        console.log("Request Estimate Data:", requestData);
        // 🔴 Delete auto-created draft estimate before final submission
        if (createdRequestId) {
          await handleDeleteDraftEstimate();
        }
        await axios.post(`${REST_API}webservice/request/add-request`, requestData)
        .then(async (res) => {
            enqueueSnackbar('Request Estimate submitted successfully', { variant: 'success' });
            if(userType != 3 && res?.data?.result && res?.data?.reqresult?.data?.length > 0){
              await handleConvertToJob(res?.data?.result, res?.data?.reqresult?.data[0])
            }
            setOpen(false);
            resetForm();
            setAvatarUrl([]);
            setCreatedRequestId(null); // Reset the auto-created request ID after successful submission
          })
          .catch((errors) => {
            console.log(errors, 'Error submitting request estimate');
          });
      } catch (error) {
        console.error('Error submitting request estimate==',error);
        enqueueSnackbar('Error submitting request estimate', { variant: 'error' });
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  const { errors, values, touched, handleSubmit, setFieldValue, getFieldProps, resetForm } = formik;

  // Same as OfficialRequestAdd: if only one Job Class exists, preselect it.
  useEffect(() => {
    if (DOMAIN !== 'la') return;
    if (!Array.isArray(projectTypes) || projectTypes.length !== 1) return;
    if (userType == 3 || isClient) return;
    if (values.project_type) return;
    setFieldValue('project_type', projectTypes[0]);
  }, [projectTypes, userType, isClient, values.project_type, setFieldValue]);

  const collaboratorOptions = useMemo(() => {
    const availableTeamMembers = Array.isArray(teamMembersOptions) ? teamMembersOptions : [];
    let filteredMembers = availableTeamMembers;
    const communitySalesRepIds = getCommunitySalesRepIds(values.community);

    if (values.community && communitySalesRepIds.length > 0) {
      const allowedIds = new Set(communitySalesRepIds.map((id) => String(id)));
      filteredMembers = availableTeamMembers.filter((member) => {
        const memberId = member?.id ?? member?.user_id;
        return memberId != null && allowedIds.has(String(memberId));
      });
    }

    if (collaborators?.length) {
      const filteredIds = new Set(
        filteredMembers.map((member) => String(member?.id ?? member?.user_id))
      );
      const missingSelections = collaborators.filter((member) => {
        const memberId = member?.id ?? member?.user_id;
        return memberId != null && !filteredIds.has(String(memberId));
      });
      if (missingSelections.length) {
        filteredMembers = [...filteredMembers, ...missingSelections];
      }
    }

    return filteredMembers;
  }, [teamMembersOptions, values.community, collaborators]);

  let totalAmount = 0;
  let totalMarkup = 0;
  let totalProductFormula = 0;
  let totalWholesalePrice = 0;
  let totalRetailPrice = 0;
  let originaltotalMarkup = 0
  let originaltotalServiceMarkup = 0
  let totalServiceMarkup = 0;
  let totalDiscount = 0

  const allServices = [ ...additionalSelectedServices];
  let selectedServicesCost = allServices?.reduce((total, service, index) => { //allServices
    const quantityFieldName = `service_quantity_${index}`;
    const quantityVal = values[quantityFieldName] ? values[quantityFieldName] : 1
    console.log("#######Service Total-------", service.quantity);
    const serviceTotal = calculateTotal(service.cost, Number(service.quantity), 0, 1);
    console.log("#######Service Total-------", serviceTotal);
    return total + parseFloat(serviceTotal?.toFixed(2));
  }, 0);
  const selectedServicesTotalCost = allServices?.reduce((total, service, index) => {
    const quantityFieldName = `service_quantity_${index}`;
    const quantityVal = values[quantityFieldName] ? values[quantityFieldName] : 1
    const serviceTotal = calculateTotal(service?.total_cost, service?.quantity, 0, 1);
    return total + parseFloat(Number(serviceTotal)?.toFixed(2));
  }, 0);
  const InitialServiceCostInProductFormula =0
  const selectedServicesSubTotal = selectedServicesTotalCost - InitialServiceCostInProductFormula
  values['labour_cost'] = selectedServicesSubTotal

  const totalsum = useMemo(() => {
    return (selectedServicesSubTotal) - parseFloat(values['invoice_discount'] || 0);
  }, [selectedServicesSubTotal, values['invoice_discount']]);
  
  // New Tax calculation
  const selectedServicesTaxTotalCost = allServices?.reduce((total, service, index) => {
    if (service?.taxable === 0) return total;
    const quantityFieldName = `service_quantity_${index}`;
    const quantityVal = values[quantityFieldName] ? values[quantityFieldName] : 1;
    const serviceTotal = calculateTotal(service?.total_cost, service?.quantity, 0, 1);
    return total + parseFloat(Number(serviceTotal)?.toFixed(2));
  }, 0);

  const selectedTaxServicesSubTotal = selectedServicesTaxTotalCost - InitialServiceCostInProductFormula
  const newTaxTotalsum = useMemo(() => {
    return (selectedTaxServicesSubTotal) - parseFloat(values['invoice_discount'] || 0);
  }, [selectedTaxServicesSubTotal, values['invoice_discount']]);
  
  const taxAmount = 0 
  const newFixedTaxAmount = selectedTaxCategory ? (selectedTaxCategory.type === 1 ? (newTaxTotalsum * (selectedTaxCategory.value || 0) / 100) : (selectedTaxCategory.value || 0) ) : 0;
  const newTaxAmount = parseFloat(parseFloat(newFixedTaxAmount).toFixed(2))

  const grandTotal =  (parseFloat(totalsum) + parseFloat(taxAmount)).toFixed(2); //old tax

  const totalJobcostSum = useMemo(() => {
    return (selectedServicesCost);
  }, [selectedServicesCost]);

  values['total'] = totalsum
  values['taxAmount'] = taxAmount
  values['budget'] = totalJobcostSum

  const DiscountTotal = (parseFloat(values['invoice_discount']) || 0);

  const handleCancel = () => {
    setOpenBackConfirm(true);
  };

  // Auto-save confirmation dialog handlers
  const handleBackConfirmYes = async () => {
    setOpenBackConfirm(false);
    try {
      // 🔴 Delete drafted estimate before submitting
      await handleDeleteDraftEstimate();
      await formik.submitForm();
    } catch (error) {
      console.error('Auto-save failed:', error);
      handleCancelAction();
    }
  };

  const handleBackConfirmNo = async () => {
    setOpenBackConfirm(false);
  
    // 🔴 Delete drafted estimate before closing
    await handleDeleteDraftEstimate();
  
    handleCancelAction();
  };

  const handleCancelAction = () => {
    setOpen(false);
    resetForm();
    setAvatarUrl([]);
    setProductRows([{ indexId: 1, product_name: '', product_desc: '', price: 0, retail_price: 0, is_product_row: 1, backendId: null, is_line_catalog:1 }]);
    setIndexCounter(1);
    setCreatedRequestId(null); // Reset the auto-created request ID
  };

  // Browser back button interception when modal is open
  useEffect(() => {
    if (!open) return;
    const handlePopState = () => {
      setOpenBackConfirm(true);
      window.history.pushState(null, '', window.location.href);
    };
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [open]);

  if (!open) {
    return null;
  }

  return (
    <>
    <FormikProvider value={formik}>
      <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
        <Grid container spacing={3}>
              <Grid item xs={12} md={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
                  <Button
                      variant="outlined"
                      startIcon={<KeyboardBackspaceOutlined />}
                      onClick={handleCancel}
                      disabled={!!autoSavingField}
                    >
                      Back
                    </Button>
                  </Box>
                <Card sx={{ p: 3 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <CardHeader sx={{ mb: 0, p: 0 }} title='Request Estimate' />
                   
                  </Stack>
                  <Stack spacing={3}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                      <Stack flexDirection={'column'}>
                        <TextField
                          required
                          sx={{ width: 400 }}
                          label="Estimate Name"
                          {...getFieldProps('name')}
                          error={Boolean(touched.name && errors.name)}
                          helperText={touched.name && errors.name}
                        />
                      </Stack>
                      <TextField
                        sx={{ width: 400 }}
                        label="#PO Number"
                        {...getFieldProps('po_number')}
                        error={Boolean(touched.po_number && errors.po_number)}
                        helperText={touched.po_number && errors.po_number}
                      />
                    </Stack>
                    
                    <LabelStyle>Description</LabelStyle>
                    <TextField
                      required
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={7}
                      label="keep the summary brief and make it communicates"
                      {...getFieldProps('description')}
                      error={Boolean(touched.description && errors.description)}
                      helperText={touched.description && errors.description}
                    />

                    <>
                      <LabelStyle>Upload Designs / Documents</LabelStyle>
                      <UploadMultiFile
                        showPreview
                        maxSize={1024 * 1024 * 1024}
                        files={avatarUrl}
                        onDrop={handleDrop}
                        onRemove={handleRemove}
                        onRemoveAll={handleRemoveAll}
                        isUploading={isUploading}
                      />
                    </>
                    {((userType !== 3 && !isClient) && (explodedPermissions?.some(permission => ['Create Estimate'].includes(permission)))) && (
                      <>
                        <Stack flexDirection={'row'} alignItems='center' justifyContent='flex-start' gap={2}>
                          {allClients?.length <= 0 ? (
                            <Stack flexDirection={'row'} alignItems='center' justifyContent='center'>
                              <CircularProgress color="primary" variant="indeterminate" />
                              <Typography variant='body2'>Loading clients...</Typography>
                            </Stack>
                          ) : (
                          <Autocomplete
                            sx={{ width: '49%' }}
                            id="combo-box-demo"
                            options={allClients}
                            value={values.clients}
                            onChange={(event, newValue) => {
                              if (newValue) {
                                setFieldValue('clients', newValue);
                                setFieldValue('community', null);
                                setSalesRepName('');
                                setSalesRepId(null);
                                setSalesRepOptions([]);
                                fetchAllCommunityByClient(newValue);
                              } else {
                                setFieldValue('clients', null);
                                setFieldValue('community', null);
                                setSalesRepName('');
                                setSalesRepId(null);
                                setSalesRepOptions([]);
                                setCommunities([]);
                              }
                            }}
                            renderInput={(params) => (
                              <TextField
                                required
                                {...params}
                                label="Clients"
                                error={Boolean(touched.clients && errors.clients)}
                                helperText={touched.clients && errors.clients}
                              />
                            )}
                            getOptionLabel={(option) => option.name}
                          />
                          )}

                          {isCommunityLoading ? (
                            <Stack flexDirection={'row'} alignItems='center' justifyContent='center'>
                              <CircularProgress color="primary" variant="indeterminate" />
                              <Typography variant='body2'>Loading projects...</Typography>
                            </Stack>
                          ) : (
                            (communities?.length > 0 ? (
                              <Autocomplete
                                sx={{ width: '49%' }}
                                id="community"
                                options={
                                  [...(communities || [])].sort((a, b) => alphanumericCompare(a?.name || '', b?.name || ''))
                                }
                                value={values.community}
                                onChange={(event, newValue) => {
                                  if (newValue) {
                                    setFieldValue('community', newValue);
                                    console.log('Selected Community:', newValue);

                                    // Sales reps dropdown: populate all sales reps for selected project
                                    const matchedCommunity = allCommunities?.find(
                                      (community) => community?.id === newValue?.id
                                    );
                                    setCollaborators([])
                                    const repOptions = getSalesRepOptionsFromCommunity(matchedCommunity);
                                    setSalesRepOptions(repOptions);
                                    if (repOptions.length > 0) {
                                      // Keep current behavior (auto-select first), but allow changing via dropdown
                                      setSalesRepName(repOptions[0]?.name || '');
                                      setSalesRepId(repOptions[0]?.id || null);
                                    } else {
                                      setSalesRepName('');
                                      setSalesRepId(null);
                                    }
                                  
                                    if (newValue.tax_category) {
                                      const taxCat = taxCategories?.find((cat) => newValue.tax_category === cat.id) || null;
                                      setSelectedTaxCategory(taxCat);
                                    }
                                  } else {
                                    setFieldValue('community', null);
                                    setSelectedTaxCategory(null);
                                    setSalesRepName('');
                                    setSalesRepId(null);
                                    setSalesRepOptions([]);
                                  }
                                }}
                                renderInput={(params) => (
                                  <Tooltip
                                    title={values.community ? formatProjectAddress(values.community) : ''}
                                  >
                                    <TextField
                                      required
                                      {...params}
                                      label="Project"
                                      error={Boolean(touched.community && errors.community)}
                                      helperText={touched.community && errors.community}
                                    />
                                  </Tooltip>
                                )}
                                getOptionLabel={(option) => option.name}
                                renderOption={(props, option) => {
                                  const { style, ...liProps } = props;
                                  return (
                                    <li
                                      {...liProps}
                                      style={{
                                        ...style,
                                        display: 'flex',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <Tooltip title={formatProjectAddress(option)}>
                                        <span
                                          style={{
                                            flex: 1,
                                            minWidth: 0,
                                            display: 'block',
                                          }}
                                        >
                                          <span
                                            style={{
                                              display: 'block',
                                              whiteSpace: 'nowrap',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                            }}
                                          >
                                            {option?.name || ''}
                                          </span>
                                          {/* <span
                                            style={{
                                              display: 'block',
                                              fontSize: 12,
                                              color: '#6b7280',
                                              whiteSpace: 'nowrap',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                            }}
                                          >
                                            {formatProjectAddress(option)}
                                          </span> */}
                                        </span>
                                      </Tooltip>
                                      <span style={{ display: 'none' }}>{option?.name || ''}</span>
                                    </li>
                                  );
                                }}
                              />
                            ) : (
                              <Stack sx={{ width: '50%' }} justifyContent={'center'}>
                                {values?.clients ? (
                                  <Typography sx={{ color: "red" }}>
                                    The selected client does not have any projects yet. Please create a new project for them!
                                  </Typography>
                                ) : (
                                  <Typography sx={{ color: "red" }}>
                                    No client selected. Please choose a client from the list.
                                  </Typography>
                                )}
                              </Stack>
                            ))
                          )}
                        </Stack>
                        {salesRepOptions?.length > 0 && (
                          <Stack sx={{ width: '100%' }}>
                            <Autocomplete
                              id="sales-rep"
                              options={salesRepOptions}
                              value={
                                salesRepOptions.find((opt) => {
                                  if (salesRepId != null && opt?.id != null) {
                                    return String(opt.id) === String(salesRepId);
                                  }
                                  return salesRepName && opt?.name === salesRepName;
                                }) || null
                              }
                              onChange={(event, newValue) => {
                                setSalesRepName(newValue?.name || '');
                                setSalesRepId(newValue?.id || null);
                              }}
                              isOptionEqualToValue={(option, value) =>
                                (option?.id != null && value?.id != null && String(option.id) === String(value.id)) ||
                                option?.name === value?.name
                              }
                              getOptionLabel={(option) => option?.name || ''}
                              renderInput={(params) => (
                                <TextField {...params} label="Sales Rep" fullWidth />
                              )}
                            />
                          </Stack>
                        )}

                        <Stack sx={{ width: '100%' }}>
                          <Autocomplete
                            multiple
                            id="collaborators"
                            options={collaboratorOptions}
                            value={collaborators}
                            onChange={(event, newValue) => setCollaborators(newValue || [])}
                            loading={teamMembersLoading}
                            isOptionEqualToValue={(option, value) => (option?.id != null && value?.id != null && String(option.id) === String(value.id))}
                            getOptionLabel={(option) => option?.name || [option?.first_name, option?.last_name].filter(Boolean).join(' ').trim() || String(option?.id || '')}
                            renderInput={(params) => (
                              <TextField {...params} label="Collaborators" fullWidth placeholder="Select Collaborators" />
                            )}
                          />
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                          {DOMAIN === 'la' && projectTypes?.length === 1 ? (
                            <TextField
                              fullWidth
                              sx={{ width: { xs: '100%', sm: '50%' } }}
                              id="combo-box-demo-type"
                              required
                              label="Job Class"
                              value={values.project_type?.name ?? projectTypes[0]?.name ?? ''}
                              InputProps={{ readOnly: true }}
                              error={Boolean(touched.project_type && errors.project_type)}
                              helperText={touched.project_type && errors.project_type}
                            />
                          ) : (
                            <Autocomplete
                              fullWidth
                              sx={{ width: { xs: '100%', sm: '50%' } }}
                              id="combo-box-demo-type"
                              options={projectTypes}
                              value={values.project_type}
                              onChange={(event, newValue) => {
                                setFieldValue('project_type', newValue);
                                if (newValue?.name !== 'TS') {
                                  setFieldValue('map_service', null);
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  required
                                  {...params}
                                  label="Job Class"
                                  error={Boolean(touched.project_type && errors.project_type)}
                                  helperText={touched.project_type && errors.project_type}
                                />
                              )}
                              getOptionLabel={(option) => option.name}
                            />
                          )}
                        <Autocomplete
                          multiple
                          fullWidth
                          sx={{ width: { xs: '100%', sm: '50%' } }}
                          id="combo-box-demo-sign-agreement"
                          options={signAgreement}
                          value={values.sign_agreement} // This should be an array of selected agreements
                          onChange={(event, newValue) => {
                            setFieldValue('sign_agreement', newValue); // Update state with selected values
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              required={false}
                              InputLabelProps={{
                                ...params.InputLabelProps,
                                required: false,
                              }}
                              label="Select Sign Agreements"
                              error={Boolean(touched.sign_agreement && errors.sign_agreement)}
                              helperText={touched.sign_agreement && errors.sign_agreement}
                            />
                          )}
                          getOptionLabel={(option) => option.name}
                          renderTags={(value, getTagProps) =>
                            value?.map((option, index) => (
                              <Chip
                                label={option.name}
                                {...getTagProps({ index })}
                                key={index}
                              />
                            ))
                          }
                        />
                        </Stack>

                        {values?.project_type?.name == 'TS' ? (
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                            <Autocomplete
                              sx={{ width: '50%' }}
                              id="combo-box-demo-category"
                              options={projectCategories}
                              value={values.project_categories}
                              onChange={(event, newValue) => {
                                setFieldValue('project_categories', newValue);
                                if (values?.project_type?.name !== 'TS' || newValue?.id != 14) {
                                  setFieldValue('map_service', null);
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Job Type"
                                  error={Boolean(touched.project_categories && errors.project_categories)}
                                  helperText={touched.project_categories && errors.project_categories}
                                />
                              )}
                              getOptionLabel={(option) => option.name}
                            />
                            {values.project_categories?.id == 14 ? (
                              <Autocomplete
                                sx={{ width: '50%' }}
                                id="combo-box-demo-type"
                                options={mapServices}
                                value={values.map_service}
                                onChange={(event, newValue) => {
                                  setFieldValue('map_service', newValue);
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    required
                                    {...params}
                                    label="Routes"
                                    error={Boolean(touched.map_service && errors.map_service)}
                                    helperText={touched.map_service && errors.map_service}
                                  />
                                )}
                                getOptionLabel={(option) => `${option.name} (${option.locations_count})`}
                              />
                            ) : ''}
                          </Stack>
                        ) : ''}
                      </>
                    )}
                  {/* 
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                      <Box
                        component={MobileDatePicker}
                        minDate={moment()}
                        label="Start Date"
                        value={moment.unix(values.start)}
                        onChange={(date) => setFieldValue('start', moment(date).unix())}
                        renderInput={(params) => (
                          <TextField size="medium" {...params} fullWidth />
                        )}
                      />
                      <Box
                        component={MobileDatePicker}
                        minDate={moment()}
                        label="Installation Date"
                        value={values.end ? moment.unix(values.end) : null}
                        onChange={(date) => {
                          setFieldValue('end', moment(date).unix());
                          setFieldValue('installation_start_date', null);
                          setFieldValue('installation_end_date', null);
                        }}
                        renderInput={(params) => (
                          <TextField
                            size="medium"
                            {...params}
                            fullWidth
                            error={Boolean(touched.end && errors.end)}
                            helperText={touched.end && errors.end}
                          />
                        )}
                      />
                    </Stack>

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1, fontStyle: 'italic' }}>
                      Choose either single installation date above OR installation date range below
                    </Typography>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <Box
                        component={MobileDatePicker}
                        minDate={moment()}
                        label="Installation Start Date"
                        value={values.installation_start_date ? moment.unix(values.installation_start_date) : null}
                        onChange={(date) => {
                          setFieldValue('installation_start_date', moment(date).unix());
                          setFieldValue('end', null);
                        }}
                        renderInput={(params) => (
                          <TextField
                            size="medium"
                            {...params}
                            fullWidth
                            error={Boolean(touched.installation_start_date && errors.installation_start_date)}
                            helperText={touched.installation_start_date && errors.installation_start_date}
                          />
                        )}
                      />
                      <Box
                        component={MobileDatePicker}
                        minDate={values.installation_start_date ? moment.unix(values.installation_start_date) : moment()}
                        label="Installation End Date"
                        value={values.installation_end_date ? moment.unix(values.installation_end_date) : null}
                        onChange={(date) => {
                          setFieldValue('installation_end_date', moment(date).unix());
                          setFieldValue('end', null);
                        }}
                        renderInput={(params) => (
                          <TextField
                            size="medium"
                            {...params}
                            fullWidth
                            error={Boolean(touched.installation_end_date && errors.installation_end_date)}
                            helperText={touched.installation_end_date && errors.installation_end_date}
                          />
                        )}
                      />
                    </Stack>

                    {(isClient || userType === 3) && (
                      <>
                        {isCommunityLoading ? (
                          <Stack
                            flexDirection={'row'}
                            alignItems="center"
                            justifyContent="center"
                            sx={{ mt: 2 }}
                          >
                            <CircularProgress
                              color="primary"
                              variant="indeterminate"
                            />
                            <Typography variant="body2">
                              Loading your projects...
                            </Typography>
                          </Stack>
                        ) : (
                          <Autocomplete
                            sx={{ width: '100%', mt: 2 }}
                            id="client-community"
                            options={clientCommunities}
                            value={values.community}
                            onChange={(event, newValue) => {
                              if (newValue) {
                                setFieldValue('community', newValue);

                                if (newValue.tax_category) {
                                  let taxCat = taxCategories?.find(cat => newValue.tax_category === cat.id) || null;
                                  setSelectedTaxCategory(taxCat);
                                }
                              } else {
                                setFieldValue('community', null);
                                setSelectedTaxCategory(null);
                              }
                            }}
                            renderInput={(params) => (
                              <Tooltip
                                title={values.community ? formatProjectAddress(values.community) : ''}
                              >
                                <TextField
                                  required
                                  {...params}
                                  label="Select Project"
                                  error={Boolean(touched.community && errors.community)}
                                  helperText={touched.community && errors.community}
                                />
                              </Tooltip>
                            )}
                            getOptionLabel={(option) => option.name}
                            renderOption={(props, option) => {
                              const { style, ...liProps } = props;
                              return (
                                <li
                                  {...liProps}
                                  style={{
                                    ...style,
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Tooltip title={formatProjectAddress(option)}>
                                    <span
                                      style={{
                                        flex: 1,
                                        minWidth: 0,
                                        display: 'block',
                                      }}
                                    >
                                      <span
                                        style={{
                                          display: 'block',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}
                                      >
                                        {option?.name || ''}
                                      </span>
                                      <span
                                        style={{
                                          display: 'block',
                                          fontSize: 12,
                                          color: '#6b7280',
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                        }}
                                      >
                                        {formatProjectAddress(option)}
                                      </span>
                                    </span>
                                  </Tooltip>
                                  <span style={{ display: 'none' }}>{option?.name || ''}</span>
                                </li>
                              );
                            }}
                          />
                        )}
                      </>
                    )} */}
                    <Divider />
                    <LabelStyle>Products</LabelStyle>
                    <TableContainer component={Paper} sx={{ mt: 2 }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            {/* <TableCell align="center">Index</TableCell> */}
                            <TableCell padding="checkbox" sx={{ width: 40 }} />
                            <TableCell align="center">Sl No</TableCell>
                            <TableCell align="center">Product Name</TableCell>
                            <TableCell align="center">Product Description</TableCell>
                            <TableCell align="center"></TableCell>
                          </TableRow>
                        </TableHead>
                        <DragDropContext onDragEnd={handleProductRowsDragEnd}>
                          <Droppable droppableId="request-estimate-product-rows">
                            {(droppableProvided) => (
                        <TableBody
                          ref={droppableProvided.innerRef}
                          {...droppableProvided.droppableProps}
                        >
                          {productRows.map((row, index) => (
                            <Draggable
                              key={`product-row-${row.indexId}`}
                              draggableId={`product-row-${row.indexId}`}
                              index={index}
                            >
                              {(provided, snapshot) => (
                            <TableRow
                              key={row.indexId}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                ...provided.draggableProps.style,
                                ...(snapshot.isDragging
                                  ? { backgroundColor: theme.palette.action.hover }
                                  : {}),
                              }}
                            >
                              <TableCell
                                padding="checkbox"
                                {...provided.dragHandleProps}
                                sx={{ width: 40, cursor: 'grab' }}
                              >
                                <DragIndicator fontSize="small" color="action" />
                              </TableCell>
                              <TableCell align="center">#{row.indexId}</TableCell>
                              <TableCell align="center">
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  value={row.product_name}
                                  onFocus={() => handleFieldFocus(row.indexId, 'name', row.product_name)}
                                  onChange={(e) => {
                                    handleProductNameChange(row.indexId, e.target.value);
                                    // Get updated row for debounce
                                    const updatedRow = { ...row, product_name: e.target.value };
                                    triggerDebouncedAutoSave(updatedRow, 'name');
                                  }}
                                  onBlur={() => handleProductRowAutoSave(row, 'name')}
                                  placeholder="Product Name"
                                  error={Boolean(rowErrors[row.indexId])}
                                  helperText={rowErrors[row.indexId]}
                                  InputProps={{
                                    endAdornment: autoSavingField?.indexId === row.indexId && autoSavingField?.field === 'name' ? (
                                      <InputAdornment position="end">
                                        <CircularProgress size={20} />
                                      </InputAdornment>
                                    ) : null
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  value={row.product_desc}
                                  onFocus={() => handleFieldFocus(row.indexId, 'desc', row.product_desc)}
                                  onChange={(e) => {
                                    handleProductDescChange(row.indexId, e.target.value);
                                    // Get updated row for debounce
                                    const updatedRow = { ...row, product_desc: e.target.value };
                                    triggerDebouncedAutoSave(updatedRow, 'desc');
                                  }}
                                  onBlur={() => handleProductRowAutoSave(row, 'desc')}
                                  placeholder="Product Description"
                                  multiline
                                  minRows={1}
                                  maxRows={3}
                                  InputProps={{
                                    endAdornment: autoSavingField?.indexId === row.indexId && autoSavingField?.field === 'desc' ? (
                                      <InputAdornment position="end">
                                        <CircularProgress size={20} />
                                      </InputAdornment>
                                    ) : null
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                  {/* {productRows.length > 1 && ( */}
                                    <IconButton 
                                      onClick={() => handleDeleteRequestEstimateProduct(row.indexId, row.backendId)}
                                      color="error"
                                    >
                                      <Delete />
                                    </IconButton>
                                  {/* )} */}
                              </TableCell>
                            </TableRow>
                              )}
                            </Draggable>
                          ))}
                          {droppableProvided.placeholder}
                        </TableBody>
                            )}
                          </Droppable>
                        </DragDropContext>
                      </Table>
                    </TableContainer>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start'}}>
                      <Button
                        variant="outlined"
                        startIcon={<Icon icon={plusFill} />}
                        onClick={addNewProductRow}
                        sx={{
                          marginLeft: '20px'
                        }}
                      >
                        Add Row
                      </Button>
                    </Box>
                    <Divider />
                    <LabelStyle>Services</LabelStyle>
                    <TableContainer component={Paper} sx={{ mt: 2 }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox" sx={{ width: 40 }} />
                            <TableCell align="center">Sl No</TableCell>
                            <TableCell align="center">Service Name</TableCell>
                            <TableCell align="center">Service Description</TableCell>
                            <TableCell align="center"></TableCell>
                          </TableRow>
                        </TableHead>
                        <DragDropContext onDragEnd={handleServiceRowsDragEnd}>
                          <Droppable droppableId="request-estimate-service-rows">
                            {(droppableProvided) => (
                        <TableBody
                          ref={droppableProvided.innerRef}
                          {...droppableProvided.droppableProps}
                        >
                          {serviceRows.map((row, index) => (
                            <Draggable
                              key={`service-row-${row.indexId}`}
                              draggableId={`service-row-${row.indexId}`}
                              index={index}
                            >
                              {(provided, snapshot) => (
                            <TableRow
                              key={row.indexId}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                ...provided.draggableProps.style,
                                ...(snapshot.isDragging
                                  ? { backgroundColor: theme.palette.action.hover }
                                  : {}),
                              }}
                            >
                              <TableCell
                                padding="checkbox"
                                {...provided.dragHandleProps}
                                sx={{ width: 40, cursor: 'grab' }}
                              >
                                <DragIndicator fontSize="small" color="action" />
                              </TableCell>
                              <TableCell align="center">#{row.indexId}</TableCell>
                              <TableCell align="center">
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  value={row.service_name}
                                  onFocus={() => handleFieldFocus(row.indexId, 'name', row.service_name)}
                                  onChange={(e) => {
                                    handleServiceNameChange(row.indexId, e.target.value);
                                    // Get updated row for debounce
                                    const updatedRow = { ...row, service_name: e.target.value };
                                    triggerDebouncedAutoSaveService(updatedRow, 'name');
                                  }}
                                  onBlur={() => handleProductRowAutoSave(row, 'name')}
                                  placeholder="Service Name"
                                  error={Boolean(rowErrors[row.indexId])}
                                  helperText={rowErrors[row.indexId]}
                                  InputProps={{
                                    endAdornment: autoSavingField?.indexId === row.indexId && autoSavingField?.field === 'name' ? (
                                      <InputAdornment position="end">
                                        <CircularProgress size={20} />
                                      </InputAdornment>
                                    ) : null
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  value={row.service_desc}
                                  onFocus={() => handleFieldFocus(row.indexId, 'desc', row.service_desc)}
                                  onChange={(e) => {
                                    handleServiceDescChange(row.indexId, e.target.value);
                                    // Get updated row for debounce
                                    const updatedRow = { ...row, service_desc: e.target.value };
                                    triggerDebouncedAutoSaveService(updatedRow, 'desc');
                                  }}
                                  onBlur={() => handleServiceRowAutoSave(row, 'desc')}
                                  placeholder="Service Description"
                                  multiline
                                  minRows={1}
                                  maxRows={3}
                                  InputProps={{
                                    endAdornment: autoSavingField?.indexId === row.indexId && autoSavingField?.field === 'desc' ? (
                                      <InputAdornment position="end">
                                        <CircularProgress size={20} />
                                      </InputAdornment>
                                    ) : null
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                  {/* {productRows.length > 1 && ( */}
                                    <IconButton 
                                      onClick={() => handleDeleteRequestEstimateService(row.indexId, row.backendId)}
                                      color="error"
                                    >
                                      <Delete />
                                    </IconButton>
                                  {/* )} */}
                              </TableCell>
                            </TableRow>
                              )}
                            </Draggable>
                          ))}
                          {droppableProvided.placeholder}
                        </TableBody>
                            )}
                          </Droppable>
                        </DragDropContext>
                      </Table>
                    </TableContainer>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start'}}>
                      <Button
                        variant="outlined"
                        startIcon={<Icon icon={plusFill} />}
                        onClick={addNewServiceRow}
                        sx={{
                          marginLeft: '20px'
                        }}
                      >
                        Add Row
                      </Button>
                    </Box>
                    {userType !==3 ?
                    <>
                      <Divider />
                      <Stack direction='row'>
                        <LabelStyleHead>Services</LabelStyleHead>
                      </Stack>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 2 }}>
                        <Autocomplete
                          // sx={{ width: 400 }}
                          fullWidth
                          multiple
                          id="combo-box-demo"
                          options={
                            [...(filteredServices?.filter(item => 
                              item.service_type == 44 &&
                              item.status === 1 && 
                              !additionalSelectedServices.some(selectedService => 
                                selectedService?.id === item?.id || selectedService?.labour_cost_service_id === item?.id
                              )
                            ) || [])].sort((a, b) => alphanumericCompare(a?.name || '', b?.name || ''))
                          }
                          value={additionalSelectedServices?.filter(item=>!item.isAdditionalProduct)}
                          // value={values.services}
                          onChange={(event, newValue) => {
                            setAdditionalSelectedServices((prevServices) => {
                              const additionals = prevServices?.filter(item => item.isAdditionalProduct === true || item.isAdditionalProduct === 1);
                              
                              // Map over the newly selected services and preserve existing properties
                              const updatedServices = newValue?.map((service) => {
                                // Check if the service already exists in the previous selected services
                                const existingService = prevServices.find(
                                  (p) => p?.id === service?.id && p?.newIndexId === service?.newIndexId
                                );

                                if (existingService) {
                                  // If service already exists, preserve all existing properties including notes
                                  return {
                                    ...existingService,
                                    quantity: existingService.quantity ?? 1
                                  };
                                } else {
                                  // If it's a new service, create it with default values
                                  return {
                                    ...service,
                                    newIndexId: serviceIndexCounter + newValue.indexOf(service) + 1,
                                    quantity: 1,
                                    note: '' // Initialize note as empty string
                                  };
                                }
                              }) || [];

                              // Update the service index counter for new services
                              if (updatedServices.length > 0) {
                                const maxIndexId = Math.max(
                                  ...updatedServices.map(s => s.newIndexId || 0),
                                  serviceIndexCounter
                                );
                                setServiceIndexCounter(maxIndexId);
                              }

                              // Update Formik's 'services' field
                              setFieldValue('services', updatedServices);
                              const finalServices = [...updatedServices, ...additionals];
                              return finalServices;
                            });
                            if(newValue.length > 0){
                              setSelectedEstimators(allEstimators || [])
                            }else{
                              setSelectedEstimators([])
                            }
                          }}
                          renderInput={(params) => (
                            <TextField {...params} required 
                            error={Boolean(touched.services && errors.services)}
                            helperText={touched.services && errors.services} 
                            label={"Select services"} />
                          )}
                          getOptionLabel={(option) =>
                            option?.name
                              ? `${option.name} (${getLabourCostServiceTabLabel(option.inhouse)})`
                              : ''
                          }
                        />
                        <Autocomplete
                          // sx={{ width: 400 }}
                          fullWidth
                          multiple
                          id="combo-box-demo"
                          options={
                            [...(allEstimators || [])].sort((a, b) => alphanumericCompare(a?.name || '', b?.name || ''))
                          }
                          value={selectedEstimators}
                          onChange={(event, newValue) => {
                            setSelectedEstimators(newValue);
                          }}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          getOptionLabel={(option) => option.name || ''}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              required
                              label="Choose Estimator"
                              error={Boolean(touched.services && errors.services)}
                              helperText={touched.services && errors.services}
                            />
                          )}
                        />
                      </Stack>
                      <LabelStyle>Selected services</LabelStyle>
                      <Scrollbar>
                        <TableContainer sx={{ minWidth: 500 }}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                {VALUE_SERVICES?.map((val) => (
                                  <TableCell>{val.value}</TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                          
                            <TableBody>
                              {additionalSelectedServices?.map((data, index) => {
                                const quantityFieldName = `service_quantity_${index}`;
                                const dataQuantity = data?.quantity ? data?.quantity : 0;
                                const dataCost = data.unit_cost || data.total_cost
                                const total = dataCost * dataQuantity
                                const markup = dataCost / Number(total)
                                data['markup'] = markup ? markup?.toFixed(2) : 0.00
                                data['quantity'] = dataQuantity
                                totalServiceMarkup += markup
                                data['total'] = total ? total?.toFixed(2) : 0.00
                                data['totalServiceMarkup'] = totalServiceMarkup?.toFixed(2)
                                let originalMarkup=data.originalMarkup||0
                                originaltotalServiceMarkup+=originalMarkup
                                // values['totalServiceMarkup'] = totalServiceMarkup?.toFixed(2)
                                console.log('total---',total);
                                console.log('data---',data);
                                const quantityDifference = Number(data.quantity)
                                data['quantityDifference'] = quantityDifference
                                return (
                                  <>
                                    <TableRow key={index}>
                                    <TableCell>{ data?.isAdditionalProduct ?
                                        <TextField
                                          required
                                          fullWidth
                                          type="text"
                                          label={'Service Name'}
                                          sx={{ width: 85 }}
                                          value={data.name}
                                          onChange={(event) => {
                                            let newValue = event.target.value

                                            // Update the selectedProducts state
                                            setAdditionalSelectedServices(
                                              (prevProducts) =>
                                                prevProducts.map((service) => {
                                                    if ( service.newIndexId === data.newIndexId ) {
                                                      return {
                                                        ...service,
                                                        name: newValue,
                                                      }
                                                    } else {
                                                      return service
                                                    }
                                                  },
                                                ),
                                            )
                                            data['name'] = newValue || ''
                                            if (isSubmitted)
                                              setIsSubmitted(false)
                                          }}
                                          error={
                                            isSubmitted && data?.name?.trim() === ''
                                          }
                                          helperText={
                                            isSubmitted && data?.name?.trim() === '' ? 'Service Name is required' : ''
                                          }
                                        />
                                        : data.service_notes ? <Typography sx={{ color: 'red', whiteSpace: 'pre-wrap' }}>{data.service_notes}</Typography> : data.name}
                                    </TableCell>
                                    <TableCell>
                                      <IconButton onClick={() => handleOpenServiceNote(data.newIndexId || data.indexId, data.service_notes)}>
                                        <NoteAdd
                                          sx={{
                                            color: (!data?.service_notes || data?.service_notes === '') ? '' : theme.palette.primary.main,
                                          }}
                                        />
                                      </IconButton>
                                    </TableCell>
                                    <TableCell>{originalMarkup ? originalMarkup?.toFixed(2) : 0.00}</TableCell>
                                    <TableCell>{data.cost}</TableCell>
                                    <TableCell>{
                                      data?.isAdditionalProduct ?
                                        <TextField
                                          required
                                          fullWidth
                                          type="number"
                                          label={'Hourly Cost'}
                                          disabled={values.project_category?.name == 'Time & Material' ? true : false}
                                          sx={{ width: 85 }}
                                          value={dataCost}
                                          onKeyPress={(event) => {
                                            if (event.key === '-') {
                                              event.preventDefault();
                                            }
                                            if (event.key === 'e') {
                                              event.preventDefault();
                                            }
                                          }}
                                          inputProps={{ min: 0 }}
                                          defaultValue={0}
                                          onChange={(event) => {
                                            let newValue = event.target.value
                                            if (newValue !== '') {
                                              setAdditionalSelectedServices((pv) => [
                                                  ...pv.map((p) => {
                                                    if ( p.newIndexId === data.newIndexId ) {
                                                      return {
                                                        ...p,
                                                        total_cost: newValue,
                                                        unit_cost: newValue,
                                                      }
                                                    } else {
                                                      return p
                                                    }
                                                  }),
                                                ],
                                              )
                                              data['total_cost'] =  newValue || 0
                                              data['unit_cost'] =  newValue || 0
                                            }
                                          }}
                                        /> : dataCost}</TableCell>
                                    <TableCell>
                                      <TextField
                                        type="number"
                                        // sx={{ width: 400 }}
                                        sx={{ minWidth: '80px' }}
                                        label={'Hours'}
                                        {...getFieldProps(quantityFieldName)}
                                        value={Number(data?.quantity) || 0}
                                        defaultValue={1}
                                        onFocus={selectInputValue}
                                        onClick={selectInputValue}
                                        onKeyPress={(event) => { 
                                          if (event.key === '-') {
                                            event.preventDefault();
                                          }
                                        }}
                                        onChange={(event) => {
                                          let newValue = event.target.value || 0;
                                          const newData = { ...data, quantity: newValue || 0 };
                                          const updatedServices = [...additionalSelectedServices];
                                          updatedServices[index] = newData;
                                          setAdditionalSelectedServices(updatedServices);
                                          setFieldValue(quantityFieldName, newValue);
                                        }}
                                        onWheel={(e) => e.target.blur()}

                                        onKeyDown={(event) => {
                                          if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                                            const newValue = event.target.value || 0;
                                            if (newValue < 1 && event.key === 'ArrowDown') {
                                              event.preventDefault(); 
                                            }
                                          }
                                        }}
                                        
                                        error={Boolean(touched[quantityFieldName] && errors[quantityFieldName])}
                                        helperText={touched[quantityFieldName] && errors[quantityFieldName]}
                                      />
                                    </TableCell>
                                    <TableCell>{data?.isAdditionalProduct ? 'N/A' : (data.cost*quantityDifference?.toFixed(2))?.toFixed(2)}</TableCell>
                                    <TableCell>{total ? total?.toFixed(2) : 0.00}</TableCell>
                                    <TableCell>{total ? total?.toFixed(2) : 0.00}</TableCell>
                                    <TableCell align='right'>
                                      <IconButton onClick={() => handleDeleteService(data.id, data.newIndexId)}>
                                        <Delete />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                  </>
                                );
                              })}
                            </TableBody>
                            {/* Service Note Modal for additionalSelectedServices */}
                            {additionalSelectedServices?.map((data, index) => (
                              <Modal
                                key={`additional-service-note-${data.indexId || data.newIndexId || index}`}
                                open={openServiceNote[data.indexId || data.newIndexId || index] || false}
                                handleClose={() => handleCloseServiceNote(data.indexId || data.newIndexId || index)}
                              >
                                <Grid
                                  container
                                  spacing={3}
                                  sx={{ justifyContent: 'center', width: '100%' }}
                                >
                                  <Grid item xs={12}>
                                    <Typography variant="h5" sx={{ mb: 2, }}>
                                      Service Note
                                    </Typography>

                                    <Stack
                                      direction={{ xs: 'column', sm: 'row' }}
                                      spacing={{ xs: 3, sm: 2 }}
                                      sx={{ mb: 2 }}
                                    >
                                      <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        size="medium"
                                        value={currentNote}
                                        onChange={handleServiceNoteChange}
                                        inputProps={{ maxLength: MAX_CHARACTERS }}
                                        helperText={`${currentNote.length}/${MAX_CHARACTERS} characters`}
                                        InputProps={{
                                          sx: {
                                            color: 'error.main',
                                          }
                                        }}
                                      />
                                    </Stack>
                                  </Grid>
                                </Grid>

                                <Grid container justifyContent="center" alignItems="center">
                                  <Grid item xs={12} md={4}>
                                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 1 }}>
                                      <LoadingButton
                                        variant="contained"
                                        onClick={() => handleSaveServiceNote(data.indexId || data.newIndexId || index)}
                                        loading={isSavingNote}
                                        disabled={!currentNote.trim()}>
                                        Save
                                      </LoadingButton>
                                      <LoadingButton
                                        variant="outlined"
                                        color="error"
                                        onClick={() => handleClearServiceNote(data.indexId || data.newIndexId || index)}
                                        loading={isClearingNote}
                                        disabled={!currentNote.trim()}>
                                        Clear
                                      </LoadingButton>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Modal>
                            ))}
                          </Table>
                        </TableContainer>
                      </Scrollbar>
                      <Stack sx={{ justifyContent: 'flex-end', alignItems: 'flex-end', display:'flex', flexDirection:'column' }}>
                        <Typography variant="h6">
                          Total Service Cost: {selectedServicesSubTotal?.toFixed(2)}
                        </Typography>
                      </Stack>

                      <Typography sx={{ color: theme.palette.error.main }}>**All values in the software are rounded to two decimal places in accordance with standard US calculation practices.</Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={'center'} spacing={{ xs: 3, sm: 2 }}>
                        <Stack sx={{ width: 400 }}>
                          <Typography >
                            Sub Total: {totalsum ? fUSPrice(totalsum) : fUSPrice(0)}
                          </Typography>
                          <Typography sx={{ color: theme.palette.mode === 'light' ? '#FFAF00' : 'yellow' }}>
                            Grand Total: {grandTotal ? fUSPrice(grandTotal) : fUSPrice(0)}
                          </Typography>
                          <Typography >Total Discount:{DiscountTotal ? DiscountTotal?.toFixed(2) : 0.00}</Typography>
                        </Stack>
                      </Stack>
                    </>
                    : '' }

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      <LoadingButton
                        type="submit"
                        variant="contained"
                        loading={isSubmitting}
                        disabled={!!autoSavingField}
                      >
                        Submit Request
                      </LoadingButton>
                      <Button variant="outlined" onClick={handleCancel} disabled={!!autoSavingField}>
                        Cancel
                      </Button>
                    </Box>
                  </Stack>
                </Card>
              </Grid>
            </Grid>
        </Form>
      </FormikProvider>

      {/* Auto-save confirmation dialog */}
      <Dialog
        open={openBackConfirm}
        onClose={() => setOpenBackConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Go back?</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Do you want to auto save this request estimate before going back?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBackConfirmNo} variant="outlined" color="primary">
            No, Go Back
          </Button>
          <Button onClick={handleBackConfirmYes} variant="contained" color="primary">
            Yes, Auto Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
